'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

export default function EarlyAccessHero() {
    const [mounted, setMounted] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animId: number;
        let t = 0;

        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);

        const draw = () => {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            ctx.clearRect(0, 0, w, h);
            t += 0.003;

            // Animated gradient orbs
            const orbs = [
                { x: w * 0.2 + Math.sin(t) * 60, y: h * 0.3 + Math.cos(t * 0.7) * 40, r: 280, color: 'rgba(55,87,255,0.07)' },
                { x: w * 0.8 + Math.cos(t * 0.8) * 50, y: h * 0.6 + Math.sin(t * 1.1) * 50, r: 320, color: 'rgba(55,87,255,0.05)' },
                { x: w * 0.5 + Math.sin(t * 1.3) * 80, y: h * 0.15 + Math.cos(t * 0.6) * 30, r: 200, color: 'rgba(100,130,255,0.06)' },
            ];

            orbs.forEach(orb => {
                const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r);
                grad.addColorStop(0, orb.color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2);
                ctx.fill();
            });

            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, [mounted]);

    return (
        <section className="pt-40 pb-16 px-6 md:px-10 relative overflow-hidden">
            {/* Animated canvas background */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ opacity: 1 }}
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `linear-gradient(rgba(55,87,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(55,87,255,0.04) 1px, transparent 1px)`,
                    backgroundSize: '60px 60px',
                }}
            />
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/3 w-[400px] h-[300px] blob-primary opacity-30" />
                <div className="absolute top-1/2 right-1/4 w-[350px] h-[250px] blob-accent opacity-25" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <span
                    className="section-label text-primary mb-4 block"
                    style={{ color: BRAND }}
                >
                    Early Access
                </span>

                <h1
                    className="text-hero font-extrabold text-foreground mb-6 max-w-3xl"
                    style={{ color: '#0D0D0D' }}
                >
                    Be among the first to{' '}
                    <span className="text-gradient-blue">experience Wisly.</span>
                </h1>

                <p
                    className="text-xl leading-relaxed max-w-xl"
                    style={{ color: '#555', fontWeight: 400 }}
                >
                    Wisly is getting ready to launch. Request early access and tell us
                    how you plan to use Wisly — whether as a student, parent, tutor, or
                    institution. We’ll send you more information when testing begins.
                </p>
            </div>
        </section>
    );
}